import posthog from 'posthog-js';

const POSTHOG_TOKEN = process.env.REACT_APP_POSTHOG_PROJECT_TOKEN;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const SLOW_API_REQUEST_MS = 3000;
const FETCH_INSTRUMENTED = '__sunridgePosthogFetchInstrumented__';
const CONSOLE_INSTRUMENTED = '__sunridgePosthogConsoleInstrumented__';

let isInitialized = false;

function configuredApiOrigins() {
  return [
    process.env.REACT_APP_API_URL,
    process.env.REACT_APP_CRM_API_URL,
    process.env.REACT_APP_CRON_API_URL,
  ]
    .filter(Boolean)
    .map(url => {
      try {
        return new URL(url).origin;
      } catch {
        return null;
      }
    })
    .filter(Boolean);
}

function apiRequestMetadata(url, baseURL) {
  if (!url || typeof window === 'undefined') return null;

  try {
    const requestUrl = new URL(url, baseURL || window.location.origin);
    if (!configuredApiOrigins().includes(requestUrl.origin)) return null;

    const [firstSegment = 'root'] = requestUrl.pathname.split('/').filter(Boolean);
    const knownAreas = new Set(['api', 'auth', 'ordered', 'patient', 'patient-profiles', 'shopify', 'treatment']);

    return {
      api_area: knownAreas.has(firstSegment) ? firstSegment : 'other',
    };
  } catch {
    return null;
  }
}

function capture(event, properties) {
  if (isInitialized) posthog.capture(event, properties);
}

function safeErrorResponseMetadata(responseData) {
  if (responseData === undefined || responseData === null) {
    return { error_response_present: false };
  }

  const errorCode = responseData && typeof responseData === 'object'
    ? responseData.errorCode || responseData.code
    : undefined;

  return {
    error_response_present: true,
    error_response_type: Array.isArray(responseData) ? 'array' : typeof responseData,
    // Only retain opaque machine codes such as INVALID_TOKEN. Messages and error
    // bodies are excluded because they can contain patient or treatment data.
    error_code: typeof errorCode === 'string' && /^[A-Z][A-Z0-9_]{1,63}$/.test(errorCode)
      ? errorCode
      : undefined,
  };
}

function captureApiResult({ url, baseURL, method, status, startedAt, failureType, responseData }) {
  const metadata = apiRequestMetadata(url, baseURL);
  if (!metadata) return;

  const durationMs = Math.round(Math.max(0, performance.now() - startedAt));
  const isFailure = !status || status >= 400;
  if (!isFailure && durationMs < SLOW_API_REQUEST_MS) return;

  capture(isFailure ? 'frontend_api_error' : 'frontend_api_slow', {
    ...metadata,
    method: (method || 'GET').toUpperCase(),
    status_code: status || 0,
    duration_ms: durationMs,
    failure_type: failureType || undefined,
    ...(isFailure ? safeErrorResponseMetadata(responseData) : {}),
  });
}

export function markAxiosRequestStarted(config) {
  config.__posthogStartedAt = performance.now();
  return config;
}

export function captureAxiosResponse(response) {
  const { config = {}, status } = response;
  captureApiResult({
    url: config.url,
    baseURL: config.baseURL,
    method: config.method,
    status,
    startedAt: config.__posthogStartedAt || performance.now(),
  });
}

export function captureAxiosError(error) {
  if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError') return;

  const config = error?.config || {};
  captureApiResult({
    url: config.url,
    baseURL: config.baseURL,
    method: config.method,
    status: error?.response?.status,
    startedAt: config.__posthogStartedAt || performance.now(),
    failureType: error?.response ? 'http_error' : 'network_error',
    responseData: error?.response?.data,
  });
}

function instrumentFetch() {
  if (window[FETCH_INSTRUMENTED]) return;
  window[FETCH_INSTRUMENTED] = true;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' || input instanceof URL ? input.toString() : input?.url;
    const method = init.method || input?.method || 'GET';
    const startedAt = performance.now();

    try {
      const response = await nativeFetch(input, init);
      captureApiResult({
        url,
        method,
        status: response.status,
        startedAt,
        responseData: response.ok ? undefined : { responseType: response.headers.get('content-type') || 'unknown' },
      });
      return response;
    } catch (error) {
      if (error?.name !== 'AbortError') {
        captureApiResult({
          url,
          method,
          startedAt,
          failureType: 'network_error',
        });
      }
      throw error;
    }
  };
}

function instrumentConsoleErrors() {
  if (window[CONSOLE_INSTRUMENTED]) return;
  window[CONSOLE_INSTRUMENTED] = true;

  const nativeConsoleError = console.error.bind(console);
  console.error = (...args) => {
    capture('frontend_console_error', {
      argument_count: args.length,
      first_argument_type: args[0] instanceof Error ? args[0].name : typeof args[0],
    });
    nativeConsoleError(...args);
  };
}

function instrumentRuntimeErrors() {
  window.addEventListener('error', event => {
    capture('frontend_runtime_error', {
      source: 'window_error',
      error_type: event.error?.name || 'Error',
    });
  });

  window.addEventListener('unhandledrejection', event => {
    capture('frontend_runtime_error', {
      source: 'unhandled_rejection',
      error_type: event.reason?.name || typeof event.reason,
    });
  });
}

export function initializePostHog() {
  if (isInitialized || !POSTHOG_TOKEN || typeof window === 'undefined') return false;

  posthog.init(POSTHOG_TOKEN, {
    api_host: POSTHOG_HOST,
    defaults: '2026-05-30',
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    capture_performance: false,
    capture_exceptions: false,
    disable_session_recording: true,
    disable_surveys: true,
  });

  isInitialized = true;
  instrumentFetch();
  instrumentConsoleErrors();
  instrumentRuntimeErrors();
  capture('frontend_loaded', { app: 'sunridge-frontend' });
  return true;
}
